// components/DiningOptions.jsx
import React from 'react';
import { Box, Grid, Typography, Button, Container, Card, CardMedia, CardContent, CardActions } from '@mui/material';
import { Link } from 'react-router-dom';

const DiningOptions = () => {
  return (
    <Box sx={{ 
      py: 12, 
      background: 'linear-gradient(to right, #1a1f2c, #0d1219)',
      backgroundImage: 'url("/images/pattern-dots.png"), linear-gradient(to right, #1a1f2c, #0d1219)',
      backgroundBlendMode: 'overlay',
      borderTop: '1px solid rgba(255,255,255,0.05)',
      borderBottom: '1px solid rgba(255,255,255,0.05)',
    }}>
      <Container maxWidth="lg">
        <Typography 
          variant="h2" 
          align="center" 
          sx={{ 
            mb: 3, 
            fontWeight: 700,
            color: 'white',
            animation: 'fadeInDown 1s ease-out',
            '@keyframes fadeInDown': {
              '0%': { opacity: 0, transform: 'translateY(-20px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' }
            } 
          }}
        >
          Dinner & A Movie
        </Typography>
        
        <Typography 
          variant="h6" 
          align="center" 
          sx={{ 
            mb: 8,
            fontWeight: 400,
            opacity: 0.9,
            maxWidth: '800px',
            mx: 'auto',
            color: 'white',
            animation: 'fadeIn 1s ease-out 0.3s both',
            '@keyframes fadeIn': {
              '0%': { opacity: 0 },
              '100%': { opacity: 0.9 }
            }
          }}
        >
          Elevate your cinema experience with our chef-curated dining options, delivered right to your luxury seat
        </Typography>
        
        <Grid container spacing={4}>
          {/* Stellar Eats Restaurant Card */}
          <Grid item xs={12} md={6} sx={{
            animation: 'slideInLeft 0.8s ease-out 0.5s both',
            '@keyframes slideInLeft': {
              '0%': { opacity: 0, transform: 'translateX(-50px)' },
              '100%': { opacity: 1, transform: 'translateX(0)' }
            }
          }}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 4,
                overflow: 'hidden',
                boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                bgcolor: 'background.paper',
                transition: 'all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                '&:hover': {
                  transform: 'translateY(-10px) scale(1.02)',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                  '& .card-media': {
                    transform: 'scale(1.1)',
                    filter: 'brightness(1.1)'
                  },
                  '& .card-button': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 5px 15px rgba(0,0,0,0.2)'
                  }
                }
              }}
            >
              <CardMedia
                component="img"
                height="300"
                image="/images/restaurant1.jpg"
                alt="Stellar Eats Restaurant"
                className="card-media"
                sx={{
                  transition: 'transform 0.8s ease, filter 0.5s ease',
                }}
              />
              <CardContent sx={{ flexGrow: 1, p: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                  Stellar Eats Restaurant
                </Typography>
                <Typography variant="body1" paragraph>
                  Enjoy a full menu of delicious appetizers, entrées, and desserts, delivered straight to your luxury seat during the movie.
                </Typography>
                <Typography variant="body1" paragraph>
                  Our chef creates special themed menu items inspired by current blockbusters, making each visit a unique culinary adventure.
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Highlights:
                  </Typography>
                  <Typography component="ul" sx={{ pl: 2 }}>
                    <li>In-seat service during films</li>
                    <li>Full bar with craft cocktails</li>
                    <li>Movie-themed specialties</li>
                    <li>Vegetarian and gluten-free options</li>
                  </Typography>
                </Box>
              </CardContent>
              <CardActions sx={{ p: 3, pt: 0 }}>
                <Button 
                  variant="contained" 
                  color="secondary" 
                  component={Link} 
                  to="/dining/menu"
                  className="card-button"
                  sx={{
                    transition: 'all 0.4s ease',
                  }}
                >
                  View Menu
                </Button>
              </CardActions>
            </Card>
          </Grid>
          
          {/* Cosmic Bar Card */}
          <Grid item xs={12} md={6} sx={{
            animation: 'slideInRight 0.8s ease-out 0.5s both',
            '@keyframes slideInRight': {
              '0%': { opacity: 0, transform: 'translateX(50px)' },
              '100%': { opacity: 1, transform: 'translateX(0)' }
            }
          }}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 4,
                overflow: 'hidden',
                boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                bgcolor: 'background.paper',
                transition: 'all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                '&:hover': {
                  transform: 'translateY(-10px) scale(1.02)',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                  '& .card-media': {
                    transform: 'scale(1.1)',
                    filter: 'brightness(1.1)'
                  },
                  '& .card-button': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 5px 15px rgba(0,0,0,0.2)'
                  }
                }
              }}
            >
              <CardMedia
                component="img"
                height="300"
                image="/images/bar2.jpg"
                alt="Cosmic Bar"
                className="card-media"
                sx={{
                  transition: 'transform 0.8s ease, filter 0.5s ease',
                }}
              />
              <CardContent sx={{ flexGrow: 1, p: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                  Cosmic Bar
                </Typography>
                <Typography variant="body1" paragraph>
                  Relax before or after your show at our stylish bar featuring craft cocktails, local beers, and a curated wine selection.
                </Typography>
                <Typography variant="body1" paragraph>
                  Try our signature movie-themed drinks or enjoy happy hour specials Monday through Thursday from 4PM to 6PM.
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Offerings:
                  </Typography>
                  <Typography component="ul" sx={{ pl: 2 }}>
                    <li>Signature movie-themed cocktails</li>
                    <li>Local craft beer rotation</li>
                    <li>Curated wine selection</li>
                    <li>Small plates and appetizers</li>
                  </Typography>
                </Box>
              </CardContent>
              <CardActions sx={{ p: 3, pt: 0 }}>
                <Button 
                  variant="contained" 
                  color="secondary" 
                  component={Link} 
                  to="/dining/reservations"
                  className="card-button"
                  sx={{
                    transition: 'all 0.4s ease',
                  }}
                >
                  Make Reservation
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default DiningOptions;